import { useState, useEffect } from 'react';
import { companyService } from '../services/companyService.js';

export const resolveCompanyNameFromValue = (company, fallback = 'Nespecificat') => {
  if (!company) return fallback;
  if (typeof company === 'object') {
    return (
      company.name ||
      company.company_name ||
      company.denumire ||
      company.code ||
      fallback
    );
  }
  return String(company);
};

export const resolveCompanyCodeFromValue = (company) => {
  if (!company) return '';
  if (typeof company === 'object') {
    return company.code || company.company_code || '';
  }
  return String(company);
};

/**
 * Custom hook to resolve company names from company codes
 * @param {string|Object} companyValue - The company code or company object to resolve
 * @returns {Object} { companyName, loading, error }
 */
export const useCompanyName = (companyValue) => {
  const [companyName, setCompanyName] = useState(resolveCompanyNameFromValue(companyValue));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyValue) {
      setCompanyName('Nespecificat');
      setLoading(false);
      return;
    }

    if (typeof companyValue === 'object') {
      setCompanyName(resolveCompanyNameFromValue(companyValue));
      setLoading(false);
      return;
    }

    const companyCode = resolveCompanyCodeFromValue(companyValue);
    if (!companyCode) {
      setCompanyName('Nespecificat');
      setLoading(false);
      return;
    }

    const fetchCompanyName = async () => {
      try {
        setLoading(true);
        setError(null);

        const name = await companyService.getCompanyName(companyCode);
        setCompanyName(name);
      } catch (err) {
        setError(err);
        setCompanyName(companyCode); // Fallback to code
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyName();
  }, [JSON.stringify(companyValue)]);

  return { companyName, loading, error };
};

/**
 * Custom hook to resolve multiple company names
 * @param {string[]} companyCodes - Array of company codes to resolve
 * @returns {Object} { companyNames, loading, error }
 */
export const useCompanyNames = (companyCodes = []) => {
  const [companyNames, setCompanyNames] = useState({});
  const [loading, setLoading] = useState(companyCodes.length > 0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyCodes || companyCodes.length === 0) {
      setCompanyNames({});
      setLoading(false);
      return;
    }

    const fetchCompanyNames = async () => {
      try {
        setLoading(true);
        setError(null);

        const promises = companyCodes.map(async (code) => {
          const name = await companyService.getCompanyName(code);
          return { code, name };
        });

        const results = await Promise.all(promises);

        const nameMap = results.reduce((acc, { code, name }) => {
          acc[code] = name;
          return acc;
        }, {});

        setCompanyNames(nameMap);
      } catch (err) {
        setError(err);
        // Fallback: use codes as names
        const fallbackNames = companyCodes.reduce((acc, code) => {
          acc[code] = code;
          return acc;
        }, {});
        setCompanyNames(fallbackNames);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyNames();
  }, [JSON.stringify(companyCodes)]);

  return { companyNames, loading, error };
};

export default useCompanyName;
